import type { ReactNode } from 'react';
import { fmtCompact, niceMax } from '../utils';

/** Axis tick: 150 000 → "150 k", 2.5 → "2,5" — small scales keep their decimal instead of rounding into duplicates. */
const fmtTick = (t: number) => (t < 1000 && !Number.isInteger(t) ? t.toLocaleString('fr-FR', { maximumFractionDigits: 1 }) : fmtCompact(t));

export type Column = {
  key: string | number;
  label: string;
  /** A second, quieter line under the label ("6 min 04 s"). */
  sub?: string;
  /** null = nothing measured: no bar, the value reads "—". */
  value: number | null;
  display: string;
  color: string;
  /** A week still to come: a dashed stub instead of a bar. */
  planned?: boolean;
  /** Hover explanation. */
  tip: string;
};

/** A run of columns with a caption under it — one season. */
export type ColumnGroup = { key: string; caption?: ReactNode; columns: Column[] };

/**
 * Vertical bars on a rounded axis — "Unique players per week" and "Time spent
 * in the app per week", grouped by season with a rule between the groups and
 * the season named underneath. Plain CSS bars rather than a charting library:
 * up to ten columns with their value printed on top need nothing more, and
 * the page stays as light as the design.
 */
export function ColumnChart({
  groups,
  height,
  unit = '',
  ticks = 3,
  gap = 24,
}: {
  groups: ColumnGroup[];
  height: number;
  /** Suffix for the axis ticks (" h" for hours). */
  unit?: string;
  ticks?: number;
  gap?: number;
}) {
  const columns = groups.flatMap((g) => g.columns);
  const max = niceMax(Math.max(0, ...columns.map((c) => c.value ?? 0)));
  // Nothing measured yet (every week still to come, or no analytics events): no
  // axis at all — a scale over nothing would invent one.
  const measured = columns.some((c) => c.value != null);
  const tickValues = measured ? Array.from({ length: ticks + 1 }, (_v, i) => (max / ticks) * (ticks - i)) : [];
  // Both rows lay the groups out the same way, so labels sit under their bars.
  const groupStyle = (g: ColumnGroup) => ({ flex: Math.max(1, g.columns.length) });
  return (
    <div className="su-chart">
      <div className="su-axis" style={{ height }} aria-hidden="true">
        {tickValues.map((t) => (
          <span key={t}>{t === 0 ? '0' : `${fmtTick(t)}${unit}`}</span>
        ))}
      </div>
      <div className="su-plot">
        <div className="su-groups su-groups-bars">
          {groups.map((g) => (
            <div key={g.key} className="su-group" style={groupStyle(g)}>
              <div className="su-columns" style={{ height, gap }}>
                {g.columns.map((c) => (
                  <div key={c.key} className="su-column" data-tip={c.tip} aria-label={c.tip}>
                    <span className="su-column-value">{c.display}</span>
                    {c.value != null && <div className="su-column-bar" style={{ height: `${(c.value / max) * 100}%`, maxWidth: 96, background: c.color }} />}
                    {c.value == null && c.planned && <div className="su-column-bar planned" />}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="su-groups">
          {groups.map((g) => (
            <div key={g.key} className="su-group" style={groupStyle(g)}>
              <div className="su-column-labels" style={{ gap }}>
                {g.columns.map((c) => (
                  <span key={c.key} className={c.planned ? 'su-muted' : undefined}>
                    {c.label}
                    {c.sub && <small>{c.sub}</small>}
                  </span>
                ))}
              </div>
              {g.caption && <div className="su-group-caption">{g.caption}</div>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
